import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../../context/AuthContext";
import { API_BASE_URL, EXPRESS_BASE_URL } from "../../../../../Helpers/apiConfig";

// media.type -> etiket (express/routes/media.js ile aynı)
const MEDIA_TYPES = { 1: "resim", 2: "ses", 3: "video" };

const MAX_COUNT = 50;

// Türkçe karakterlere duyarsız arama
const norm = (s) => String(s || "").toLocaleLowerCase("tr-TR").trim();

// Kategori ağacını "Ana › Alt" yollarıyla düz listeye çevirir
const flattenTree = (nodes, parentPath = "", list = []) => {
    for (const n of nodes || []) {
        const path = parentPath ? `${parentPath} › ${n.name}` : n.name;
        list.push({ id: n.id, name: n.name, path, childCount: (n.children || []).length });
        flattenTree(n.children, path, list);
    }
    return list;
};

// Basit modal (sayfadaki diğer AI ekranlarıyla aynı yapı)
const Modal = ({ title, onClose, children, footer }) => (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={onClose}>
        <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title fw-bold">{title}</h5>
                    <button type="button" className="btn-close" onClick={onClose}></button>
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-footer">{footer}</div>}
            </div>
        </div>
    </div>
);

export const AdminAiBulkQuestion = () => {
    const { user } = useAuth();
    const headers = { Authorization: `Bearer ${user?.token}` };

    // --- 1. Kurs / Konu ---
    const [courses, setCourses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [courseId, setCourseId] = useState("");
    const [subjectId, setSubjectId] = useState("");
    const [nextSortId, setNextSortId] = useState(null);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [error, setError] = useState("");

    // Üstteki arama: tüm kursların konuları (ilk aramada bir kez yüklenir)
    const [search, setSearch] = useState("");
    const [searchIndex, setSearchIndex] = useState(null);
    const [indexing, setIndexing] = useState(false);

    // --- 2. Medya kategorisi ---
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [categories, setCategories] = useState(null);
    const [categorySearch, setCategorySearch] = useState("");
    const [category, setCategory] = useState(null);
    const [mediaList, setMediaList] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);

    // --- 3. Soru alt türü ---
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templates, setTemplates] = useState(null);
    const [typeLabels, setTypeLabels] = useState({});
    const [templateSearch, setTemplateSearch] = useState("");
    const [template, setTemplate] = useState(null);

    // --- 4. Adet ---
    const [count, setCount] = useState("10");

    const apiGet = (url, params) => axios.get(url, { headers, params });

    // Kurslar
    useEffect(() => {
        const load = async () => {
            try {
                const res = await apiGet(`${API_BASE_URL}/admin/CourseManager/GetCourses`);
                if (res.data.success) {
                    setCourses(res.data.data.filter((c) => !c.isDeleted && c.isActive));
                } else {
                    setError(res.data.message || "Kurslar yüklenemedi.");
                }
            } catch (err) {
                setError(err.response?.data?.message || err.message);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSubjects = async (cid) => {
        const res = await apiGet(`${API_BASE_URL}/admin/SubjectManager/GetListByCourse`, { id: cid });
        if (!res.data.success) return [];
        return res.data.data.filter((s) => s.isActive).sort((a, b) => a.sortId - b.sortId);
    };

    // Konuya eklenecek ilk sıra numarası (Soru Oluştur sayfasındaki mantık)
    const fetchNextSortId = async (sid) => {
        setNextSortId(null);
        if (!sid) return;
        try {
            const res = await apiGet(`${API_BASE_URL}/admin/ContentManager/GetListBySubject`, { subjectId: sid });
            if (res.data.success) {
                const list = res.data.data || [];
                setNextSortId(list.length ? list[list.length - 1].sortId + 1 : 1);
            }
        } catch {
            // sıra numarası sadece bilgi amaçlı
        }
    };

    const selectCourse = async (cid, thenSubjectId = "") => {
        setCourseId(cid);
        setSubjectId("");
        setSubjects([]);
        setNextSortId(null);
        if (!cid) return;
        setLoadingSubjects(true);
        try {
            setSubjects(await fetchSubjects(cid));
            if (thenSubjectId) {
                setSubjectId(String(thenSubjectId));
                fetchNextSortId(thenSubjectId);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
        setLoadingSubjects(false);
    };

    const selectSubject = (sid) => {
        setSubjectId(sid);
        fetchNextSortId(sid);
    };

    // Arama kutusuna ilk yazıldığında tüm konuları paralel çek
    const ensureSearchIndex = async () => {
        if (searchIndex || indexing || !courses.length) return;
        setIndexing(true);
        try {
            const all = await Promise.all(
                courses.map(async (c) => (await fetchSubjects(c.id)).map((s) => ({ course: c, subject: s })))
            );
            setSearchIndex(all.flat());
        } catch {
            setSearchIndex([]);
        }
        setIndexing(false);
    };

    const searchResults = useMemo(() => {
        const q = norm(search);
        if (!q) return [];
        const courseHits = courses
            .filter((c) => norm(c.courseName).includes(q) || norm(c.courseDesc).includes(q))
            .map((c) => ({ key: `c${c.id}`, course: c, subject: null }));
        const subjectHits = (searchIndex || [])
            .filter(({ course, subject }) =>
                norm(subject.subjectName).includes(q) || norm(subject.subjectDesc).includes(q) || norm(course.courseName).includes(q))
            .map(({ course, subject }) => ({ key: `s${subject.id}`, course, subject }));
        return [...courseHits, ...subjectHits].slice(0, 30);
    }, [search, courses, searchIndex]);

    const pickSearchResult = (r) => {
        setSearch("");
        selectCourse(String(r.course.id), r.subject ? r.subject.id : "");
    };

    // --- Medya modalı ---
    const openMediaModal = async () => {
        setShowMediaModal(true);
        if (categories) return;
        try {
            const res = await apiGet(`${EXPRESS_BASE_URL}/api/categories/tree`);
            setCategories(res.data.success ? flattenTree(res.data.data) : []);
        } catch {
            setCategories([]);
        }
    };

    const pickCategory = async (cat) => {
        setCategory(cat);
        setMediaList([]);
        setLoadingMedia(true);
        try {
            const res = await apiGet(`${EXPRESS_BASE_URL}/api/media/bycategory/${cat.id}`);
            setMediaList(res.data.success ? res.data.data : []);
        } catch {
            setMediaList([]);
        }
        setLoadingMedia(false);
    };

    const filteredCategories = useMemo(() => {
        const q = norm(categorySearch);
        return (categories || []).filter((c) => !q || norm(c.path).includes(q));
    }, [categories, categorySearch]);

    // --- Alt tür modalı ---
    const openTemplateModal = async () => {
        setShowTemplateModal(true);
        if (templates) return;
        try {
            const [rulesRes, listRes] = await Promise.all([
                apiGet(`${EXPRESS_BASE_URL}/api/question-templates/rules`),
                apiGet(`${EXPRESS_BASE_URL}/api/question-templates`, { active: 1 }),
            ]);
            const labels = {};
            Object.entries(rulesRes.data.data?.types || {}).forEach(([k, v]) => { labels[k] = v.label || k; });
            setTypeLabels(labels);
            setTemplates(listRes.data.success ? listRes.data.data : []);
        } catch {
            setTemplates([]);
        }
    };

    const filteredTemplates = useMemo(() => {
        const q = norm(templateSearch);
        return (templates || []).filter((t) =>
            !q || norm(t.name).includes(q) || norm(t.description).includes(q) || norm(typeLabels[t.content_type] || t.content_type).includes(q));
    }, [templates, templateSearch, typeLabels]);

    // --- Özet ---
    const selectedCourse = courses.find((c) => String(c.id) === String(courseId));
    const selectedSubject = subjects.find((s) => String(s.id) === String(subjectId));
    const countNum = Number(count);
    const countValid = Number.isInteger(countNum) && countNum >= 1 && countNum <= MAX_COUNT;
    const ready = !!(selectedSubject && category && template && countValid);

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="card shadow-sm border-0 mb-4 p-3">
                <h3 className="fw-bold text-primary m-0">AI Toplu Soru Oluştur</h3>
                <small className="text-muted">Soruların kaydedileceği yeri, kullanılacak medyayı ve soru alt türünü seçin</small>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {/* 1. KONUM */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <h5 className="fw-bold mb-3">1. Soruların kaydedileceği yer</h5>

                    <div className="position-relative mb-3">
                        <input
                            type="search"
                            className="form-control"
                            placeholder="Kurs veya konu ara..."
                            value={search}
                            onFocus={ensureSearchIndex}
                            onChange={(e) => { setSearch(e.target.value); ensureSearchIndex(); }}
                        />
                        {search && (
                            <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 10, maxHeight: 320, overflowY: "auto" }}>
                                {indexing && <div className="list-group-item small text-muted">Konular yükleniyor...</div>}
                                {!indexing && searchResults.length === 0 && <div className="list-group-item small text-muted">Sonuç bulunamadı.</div>}
                                {searchResults.map((r) => (
                                    <button type="button" key={r.key} className="list-group-item list-group-item-action" onClick={() => pickSearchResult(r)}>
                                        {r.subject ? (
                                            <>
                                                <span className="text-muted small">{r.course.courseName} › </span>
                                                <strong>{r.subject.subjectName}</strong>
                                                {r.subject.subjectDesc && <span className="text-muted small"> — {r.subject.subjectDesc}</span>}
                                            </>
                                        ) : (
                                            <><span className="badge bg-secondary me-2">Kurs</span><strong>{r.course.courseName}</strong></>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Kurs</label>
                            <select className="form-select" value={courseId} onChange={(e) => selectCourse(e.target.value)}>
                                <option value="">Kurs seçin</option>
                                {courses.map((c) => (
                                    <option key={c.id} value={c.id}>{c.courseName}{c.courseDesc ? ` -- ${c.courseDesc}` : ""}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Konu</label>
                            <select className="form-select" value={subjectId} onChange={(e) => selectSubject(e.target.value)} disabled={!courseId || loadingSubjects}>
                                <option value="">{loadingSubjects ? "Yükleniyor..." : "Konu seçin"}</option>
                                {subjects.map((s) => (
                                    <option key={s.id} value={s.id}>{s.subjectName}{s.subjectDesc ? ` // ${s.subjectDesc}` : ""}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {selectedSubject && nextSortId && (
                        <div className="small text-muted mt-2">Yeni sorular bu konuda {nextSortId}. sıradan itibaren eklenecek.</div>
                    )}
                </div>
            </div>

            <div className="row g-4 mb-4">
                {/* 2. MEDYA */}
                <div className="col-lg-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-body">
                            <h5 className="fw-bold mb-3">2. Medya kategorisi</h5>
                            {category ? (
                                <>
                                    <div className="mb-2"><strong>{category.path}</strong> <span className="badge bg-info text-dark">{mediaList.length} medya</span></div>
                                    {mediaList.length > 0 && (
                                        <div className="d-flex flex-wrap gap-1 mb-3">
                                            {mediaList.slice(0, 8).map((m) => (
                                                m.type === 1
                                                    ? <img key={m.id} src={m.url} alt={m.name} title={m.name} className="rounded border" style={{ width: 48, height: 48, objectFit: "cover" }} />
                                                    : <span key={m.id} className="badge bg-light text-dark border" title={m.name}>{MEDIA_TYPES[m.type]}: {m.name}</span>
                                            ))}
                                            {mediaList.length > 8 && <span className="small text-muted align-self-center">+{mediaList.length - 8}</span>}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-muted small">Sorularda kullanılacak medyaların kategorisini seçin.</p>
                            )}
                            <button type="button" className="btn btn-outline-primary" onClick={openMediaModal}>
                                {category ? "Kategoriyi değiştir" : "Kategori seç"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 3. ALT TÜR */}
                <div className="col-lg-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-body">
                            <h5 className="fw-bold mb-3">3. Soru alt türü</h5>
                            {template ? (
                                <div className="mb-3">
                                    <strong>{template.name}</strong>{" "}
                                    <span className="badge bg-info text-dark">{typeLabels[template.content_type] || template.content_type}</span>
                                    {template.description && <div className="small text-muted mt-1">{template.description}</div>}
                                </div>
                            ) : (
                                <p className="text-muted small">Oluşturulacak soruların alt türünü seçin.</p>
                            )}
                            <button type="button" className="btn btn-outline-primary" onClick={openTemplateModal}>
                                {template ? "Alt türü değiştir" : "Alt tür seç"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. ADET */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <h5 className="fw-bold mb-3">4. Soru adedi</h5>
                    <input
                        type="number"
                        className={`form-control ${count && !countValid ? "is-invalid" : ""}`}
                        style={{ maxWidth: 200 }}
                        min="1"
                        max={MAX_COUNT}
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                    />
                    <div className="invalid-feedback">1 ile {MAX_COUNT} arasında bir sayı girin.</div>
                </div>
            </div>

            {/* ÖZET */}
            <div className="card shadow-sm border-0">
                <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <ul className="list-unstyled small m-0">
                        <li>{selectedSubject ? "✅" : "⬜"} Konum: {selectedSubject ? `${selectedCourse?.courseName} › ${selectedSubject.subjectName}` : "seçilmedi"}</li>
                        <li>{category ? "✅" : "⬜"} Medya: {category ? `${category.path} (${mediaList.length})` : "seçilmedi"}</li>
                        <li>{template ? "✅" : "⬜"} Alt tür: {template ? template.name : "seçilmedi"}</li>
                        <li>{countValid ? "✅" : "⬜"} Adet: {countValid ? countNum : "geçersiz"}</li>
                    </ul>
                    {/* Prompt oluşturma ve gönderim sonraki adımda bağlanacak */}
                    <button type="button" className="btn btn-primary btn-lg" disabled title="Prompt adımı henüz eklenmedi">
                        Soruları Oluştur
                    </button>
                </div>
                {!ready && <div className="card-footer bg-white small text-muted border-0 pt-0">Devam etmek için tüm adımları tamamlayın.</div>}
            </div>

            {/* MEDYA KATEGORİSİ MODALI */}
            {showMediaModal && (
                <Modal
                    title="Medya kategorisi seç"
                    onClose={() => setShowMediaModal(false)}
                    footer={<button type="button" className="btn btn-primary" onClick={() => setShowMediaModal(false)} disabled={!category}>Seçimi kullan</button>}
                >
                    <input
                        type="search"
                        className="form-control mb-3"
                        placeholder="Kategori adı ara..."
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        autoFocus
                    />
                    {categories === null && <div className="text-muted small">Kategoriler yükleniyor...</div>}
                    {categories && (
                        <div className="list-group mb-3" style={{ maxHeight: 220, overflowY: "auto" }}>
                            {filteredCategories.length === 0 && <div className="list-group-item small text-muted">Kategori bulunamadı.</div>}
                            {filteredCategories.map((c) => (
                                <button
                                    type="button"
                                    key={c.id}
                                    className={`list-group-item list-group-item-action ${category?.id === c.id ? "active" : ""}`}
                                    onClick={() => pickCategory(c)}
                                >
                                    {c.path}
                                </button>
                            ))}
                        </div>
                    )}

                    {category && (
                        <>
                            <h6 className="fw-bold">{category.path} — medyalar</h6>
                            {loadingMedia && <div className="text-muted small">Medyalar yükleniyor...</div>}
                            {!loadingMedia && mediaList.length === 0 && (
                                <div className="alert alert-warning small mb-0">Bu kategoride medya yok.</div>
                            )}
                            {!loadingMedia && mediaList.length > 0 && (
                                <>
                                    <p className="small text-muted mb-2">
                                        Bu {mediaList.length} medya AI'a referans olarak gönderilecek (id, isim, tür, url).
                                    </p>
                                    <div className="table-responsive" style={{ maxHeight: 260 }}>
                                        <table className="table table-sm align-middle small mb-0">
                                            <thead className="table-light"><tr><th>id</th><th></th><th>isim</th><th>tür</th><th>url</th></tr></thead>
                                            <tbody>
                                                {mediaList.map((m) => (
                                                    <tr key={m.id}>
                                                        <td>{m.id}</td>
                                                        <td>{m.type === 1 && <img src={m.url} alt="" style={{ width: 32, height: 32, objectFit: "cover" }} className="rounded" />}</td>
                                                        <td className="fw-semibold">{m.name}</td>
                                                        <td>{MEDIA_TYPES[m.type] || m.type}</td>
                                                        <td className="text-truncate" style={{ maxWidth: 220 }}><a href={m.url} target="_blank" rel="noreferrer">{m.url}</a></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal>
            )}

            {/* SORU ALT TÜRÜ MODALI */}
            {showTemplateModal && (
                <Modal title="Soru alt türü seç" onClose={() => setShowTemplateModal(false)}>
                    <input
                        type="search"
                        className="form-control mb-3"
                        placeholder="Alt tür adı veya soru türü ara..."
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        autoFocus
                    />
                    {templates === null && <div className="text-muted small">Alt türler yükleniyor...</div>}
                    {templates && filteredTemplates.length === 0 && <div className="text-muted small">Alt tür bulunamadı.</div>}
                    <div className="list-group">
                        {filteredTemplates.map((t) => (
                            <button
                                type="button"
                                key={t.id}
                                className={`list-group-item list-group-item-action ${template?.id === t.id ? "active" : ""}`}
                                onClick={() => { setTemplate(t); setShowTemplateModal(false); }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <strong>{t.name}</strong>
                                    <span className={`badge ${template?.id === t.id ? "bg-light text-dark" : "bg-info text-dark"}`}>
                                        {typeLabels[t.content_type] || t.content_type}
                                    </span>
                                </div>
                                {t.description && <div className="small mt-1 opacity-75">{t.description}</div>}
                            </button>
                        ))}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminAiBulkQuestion;
